import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Alert, Spinner, Modal, Form, Table, Badge } from 'react-bootstrap';
import {
  getMarketingEmails,
  postMarketingEmails,
  putMarketingEmailsEmailId,
  deleteMarketingEmailsEmailId,
  getMarketingEmailsSenders,
  postMarketingEmailsEmailIdSend,
  getMarketingEmailLists,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import { MarketingEmailSendButton } from '../components/MarketingEmailSendButton';

interface MarketingEmail {
  id?: string;
  name?: string;
  subject?: string;
  html_content?: string;
  plain_content?: string;
  created_at?: string;
  updated_at?: string;
}

interface MarketingEmailSender {
  id?: number;
  name?: string;
  email?: string;
  verified?: boolean;
}

interface EmailList {
  id?: string;
  name?: string;
  contact_count?: number;
}

const EmailTemplatesTab: React.FC = () => {
  const [emails, setEmails] = useState<MarketingEmail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create/Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmail, setEditingEmail] = useState<MarketingEmail | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    html_content: '',
    plain_content: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewEmail, setPreviewEmail] = useState<MarketingEmail | null>(null);
  const [previewMode, setPreviewMode] = useState<'html' | 'plain'>('html');

  // Send modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState<MarketingEmail | null>(null);
  const [senders, setSenders] = useState<MarketingEmailSender[]>([]);
  const [lists, setLists] = useState<EmailList[]>([]);
  const [selectedSenderId, setSelectedSenderId] = useState<number | null>(null);
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
  const [isLoadingSendData, setIsLoadingSendData] = useState(false);
  const [sendMode, setSendMode] = useState<'now' | 'scheduled'>('now');
  const [scheduledDateTime, setScheduledDateTime] = useState<string>('');

  // Preview scroll ref
  const previewContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getMarketingEmails();
      setEmails(response.emails || []);
    } catch (err) {
      console.error('Error fetching marketing emails:', err);
      setError('Failed to fetch email templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingEmail(null);
    setFormData({
      name: '',
      subject: '',
      html_content: '',
      plain_content: '',
    });
    setShowEditModal(true);
  };

  const handleEdit = (email: MarketingEmail) => {
    setEditingEmail(email);
    setFormData({
      name: email.name || '',
      subject: email.subject || '',
      html_content: email.html_content || '',
      plain_content: email.plain_content || '',
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      if (!formData.name.trim()) {
        setError('Please enter a template name');
        setIsSaving(false);
        return;
      }
      if (!formData.subject.trim()) {
        setError('Please enter a subject line');
        setIsSaving(false);
        return;
      }

      if (editingEmail?.id) {
        await putMarketingEmailsEmailId(editingEmail.id, {
          name: formData.name,
          subject: formData.subject,
          html_content: formData.html_content,
          plain_content: formData.plain_content,
        });
      } else {
        await postMarketingEmails({
          name: formData.name,
          subject: formData.subject,
          html_content: formData.html_content,
          plain_content: formData.plain_content,
        });
      }

      setShowEditModal(false);
      await fetchEmails();
    } catch (err: any) {
      console.error('Error saving email template:', err);
      setError('Failed to save email template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (email: MarketingEmail) => {
    if (!email.id) return;
    if (!window.confirm(`Are you sure you want to delete the template "${email.name}"?`)) {
      return;
    }

    try {
      await deleteMarketingEmailsEmailId(email.id);
      await fetchEmails();
    } catch (err) {
      console.error('Error deleting email template:', err);
      setError('Failed to delete email template');
    }
  };

  const handlePreview = (email: MarketingEmail) => {
    setPreviewEmail(email);
    setPreviewMode('html');
    setShowPreviewModal(true);
  };

  const handleOpenSendModal = async (email: MarketingEmail) => {
    setSendingEmail(email);
    setSelectedSenderId(null);
    setSelectedListIds([]);
    setSendMode('now');
    setScheduledDateTime('');
    setShowSendModal(true);
    setIsLoadingSendData(true);

    try {
      const [sendersResponse, listsResponse] = await Promise.all([
        getMarketingEmailsSenders(),
        getMarketingEmailLists(),
      ]);
      setSenders(sendersResponse.senders || []);
      setLists(listsResponse || []);
    } catch (err) {
      console.error('Error loading send data:', err);
      setError('Failed to load senders and lists');
    } finally {
      setIsLoadingSendData(false);
    }
  };

  // initiateSend just POSTs and returns the send_id. The MarketingEmailSendButton
  // takes over from there — owning the spinner, tooltip progress, and terminal
  // state, then firing onAccepted/onFailed callbacks below.
  const initiateSend = async (): Promise<string | undefined> => {
    if (!sendingEmail?.id || !selectedSenderId || selectedListIds.length === 0) {
      setError('Please select a sender and at least one list');
      return undefined;
    }

    if (sendMode === 'scheduled' && !scheduledDateTime) {
      setError('Please select a date and time for scheduling');
      return undefined;
    }

    setError(null);

    try {
      const requestBody: { sender_id: number; list_ids: string[]; schedule_at?: string } = {
        sender_id: selectedSenderId,
        list_ids: selectedListIds,
      };

      if (sendMode === 'scheduled' && scheduledDateTime) {
        requestBody.schedule_at = new Date(scheduledDateTime).toISOString();
      }

      const response = await postMarketingEmailsEmailIdSend(sendingEmail.id, requestBody);
      return (response as any)?.send_id as string | undefined;
    } catch (err: any) {
      console.error('Error initiating send:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to start send');
      return undefined;
    }
  };

  const handleSendAccepted = () => {
    const name = sendingEmail?.name || 'email';
    setShowSendModal(false);
    const actionText = sendMode === 'now'
      ? 'handed off to SendGrid for delivery'
      : `scheduled with SendGrid for ${new Date(scheduledDateTime).toLocaleString()}`;
    alert(`Email "${name}" has been ${actionText}. Delivery progress will appear in the send history as recipients are reached.`);
  };

  const handleSendFailed = (errMsg: string) => {
    setError(`Send failed: ${errMsg}`);
  };

  const toggleListSelection = (listId: string) => {
    setSelectedListIds(prev =>
      prev.includes(listId)
        ? prev.filter(id => id !== listId)
        : [...prev, listId]
    );
  };

  return (
    <>
      <Card className="">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <h5 className="mb-1">Email Templates</h5>
              <p className="text-muted mb-0">Create and manage marketing email templates</p>
            </div>
            <Button variant="primary" onClick={handleCreate}>
              <i className="bi bi-plus-circle me-1"></i>
              Create Template
            </Button>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ whiteSpace: 'pre-wrap' }}>
              {error}
            </Alert>
          )}

          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading templates...</p>
            </div>
          ) : emails.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-envelope-paper display-4 text-muted"></i>
              <h6 className="mt-3 text-muted">No Email Templates</h6>
              <p className="text-muted">Create your first email template to get started</p>
            </div>
          ) : (
            <Table hover responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Subject</th>
                  <th>Content</th>
                  <th>Updated</th>
                  <th style={{ width: '200px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((email) => (
                  <tr key={email.id}>
                    <td className="fw-medium">{email.name}</td>
                    <td>{email.subject}</td>
                    <td>
                      {email.html_content && <Badge bg="info" className="me-1">HTML</Badge>}
                      {email.plain_content && <Badge bg="secondary">Plain</Badge>}
                      {!email.html_content && !email.plain_content && (
                        <span className="text-muted">No content</span>
                      )}
                    </td>
                    <td>
                      {email.updated_at
                        ? new Date(email.updated_at).toLocaleDateString()
                        : '-'
                      }
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handlePreview(email)}
                          title="Preview"
                        >
                          <i className="bi bi-eye"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleEdit(email)}
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleOpenSendModal(email)}
                          title="Send"
                        >
                          <i className="bi bi-send"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(email)}
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingEmail ? 'Edit Email Template' : 'Create Email Template'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ whiteSpace: 'pre-wrap' }}>
              {error}
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Template Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Monthly Newsletter"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              maxLength={255}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Subject Line <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Your November Newsletter is here!"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              maxLength={500}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>HTML Content</Form.Label>
            <Form.Control
              type="file"
              accept=".html,.htm"
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    setFormData({ ...formData, html_content: event.target?.result as string || '' });
                  };
                  reader.readAsText(file);
                }
              }}
            />
            {formData.html_content && (
              <div className="mt-2">
                <Badge bg="success" className="me-2">HTML loaded</Badge>
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                  {new TextEncoder().encode(formData.html_content).length.toLocaleString()} bytes
                </span>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 ms-2"
                  onClick={() => setFormData({ ...formData, html_content: '' })}
                >
                  Clear
                </Button>
              </div>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Plain Text Content</Form.Label>
            <Form.Control
              type="file"
              accept=".txt"
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    setFormData({ ...formData, plain_content: event.target?.result as string || '' });
                  };
                  reader.readAsText(file);
                }
              }}
            />
            {formData.plain_content && (
              <div className="mt-2">
                <Badge bg="success" className="me-2">Plain text loaded</Badge>
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                  {new TextEncoder().encode(formData.plain_content).length.toLocaleString()} bytes
                </span>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 ms-2"
                  onClick={() => setFormData({ ...formData, plain_content: '' })}
                >
                  Clear
                </Button>
              </div>
            )}
            <Form.Text className="text-muted">
              Optional but recommended for email clients that don't support HTML
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleSave}
            disabled={isSaving || !formData.name.trim() || !formData.subject.trim()}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-1"></i>
                {editingEmail ? 'Save Changes' : 'Create Template'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Preview Modal */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Preview: {previewEmail?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <strong>Subject:</strong> {previewEmail?.subject}
          </div>
          <div className="mb-3">
            <Button
              variant={previewMode === 'html' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => {
                setPreviewMode('html');
                if (previewContainerRef.current) {
                  previewContainerRef.current.scrollTop = 0;
                }
              }}
              className="me-2"
            >
              HTML
            </Button>
            <Button
              variant={previewMode === 'plain' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => {
                setPreviewMode('plain');
                if (previewContainerRef.current) {
                  previewContainerRef.current.scrollTop = 0;
                }
              }}
            >
              Plain Text
            </Button>
          </div>
          <div
            ref={previewContainerRef}
            className="border rounded"
            style={{
              height: '400px',
              overflow: 'hidden',
              backgroundColor: 'var(--bs-body-bg)'
            }}
          >
            {previewMode === 'html' ? (
              previewEmail?.html_content ? (
                <iframe
                  srcDoc={previewEmail.html_content}
                  title="Email Preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  sandbox="allow-same-origin"
                />
              ) : (
                <p className="text-muted p-3">No HTML content</p>
              )
            ) : (
              previewEmail?.plain_content ? (
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    margin: 0,
                    padding: '1rem',
                    height: '100%',
                    overflow: 'auto',
                  }}
                >
                  {previewEmail.plain_content}
                </pre>
              ) : (
                <p className="text-muted p-3">No plain text content</p>
              )
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Send Modal */}
      <Modal show={showSendModal} onHide={() => setShowSendModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Send: {sendingEmail?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ whiteSpace: 'pre-wrap' }}>
              {error}
            </Alert>
          )}

          {isLoadingSendData ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading senders and lists...</p>
            </div>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>From Sender <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={selectedSenderId || ''}
                  onChange={(e) => setSelectedSenderId(Number(e.target.value) || null)}
                >
                  <option value="">Select a sender...</option>
                  {senders.filter(s => s.verified).map((sender) => (
                    <option key={sender.id} value={sender.id}>
                      {sender.name} &lt;{sender.email}&gt;
                    </option>
                  ))}
                </Form.Select>
                {senders.length === 0 && (
                  <Form.Text className="text-warning">
                    No verified senders found. Sync senders from SendGrid settings first.
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Send to Lists <span className="text-danger">*</span></Form.Label>
                <div className="border rounded p-2" style={{ maxHeight: '200px', overflow: 'auto' }}>
                  {lists.length === 0 ? (
                    <p className="text-muted mb-0">No lists found</p>
                  ) : (
                    lists.map((list) => (
                      <Form.Check
                        key={list.id}
                        type="checkbox"
                        id={`list-${list.id}`}
                        label={
                          <span>
                            {list.name}
                            <Badge bg="secondary" className="ms-2">{list.contact_count || 0} contacts</Badge>
                          </span>
                        }
                        checked={selectedListIds.includes(list.id || '')}
                        onChange={() => list.id && toggleListSelection(list.id)}
                      />
                    ))
                  )}
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>When to Send</Form.Label>
                <div>
                  <Form.Check
                    type="radio"
                    id="send-now"
                    name="sendMode"
                    label="Send Now"
                    checked={sendMode === 'now'}
                    onChange={() => setSendMode('now')}
                  />
                  <Form.Check
                    type="radio"
                    id="send-scheduled"
                    name="sendMode"
                    label="Schedule for later"
                    checked={sendMode === 'scheduled'}
                    onChange={() => setSendMode('scheduled')}
                  />
                </div>
                {sendMode === 'scheduled' && (
                  <Form.Control
                    type="datetime-local"
                    className="mt-2"
                    value={scheduledDateTime}
                    onChange={(e) => setScheduledDateTime(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                  />
                )}
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowSendModal(false)}>
            Cancel
          </Button>
          <MarketingEmailSendButton
            claimId={`email-send-${sendingEmail?.id || 'unknown'}`}
            onClick={initiateSend}
            onAccepted={handleSendAccepted}
            onFailed={handleSendFailed}
            disabled={!selectedSenderId || selectedListIds.length === 0 || (sendMode === 'scheduled' && !scheduledDateTime)}
            buttonText={sendMode === 'now' ? 'Send Now' : 'Schedule Send'}
            sendingText={sendMode === 'now' ? 'Sending...' : 'Scheduling...'}
            icon={sendMode === 'now' ? 'bi-send' : 'bi-calendar-check'}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EmailTemplatesTab;
